import React from "react";
import { Star, Heart, Send, Sparkles, Clock, TrendingUp, Trophy, AlertOctagon } from "lucide-react";

const logoTone = {
  rose:    "bg-rose-100 text-rose-700",
  blue:    "bg-blue-100 text-blue-700",
  violet:  "bg-indigo-100 text-indigo-700",
  amber:   "bg-amber-100 text-amber-700",
  emerald: "bg-indigo-100 text-indigo-700",
};

const badgeMeta = {
  "Top performer":  "bg-indigo-50 text-indigo-700 border-indigo-200",
  "AI recommended": "bg-indigo-50 text-indigo-700 border-indigo-200",
  Watch:            "bg-rose-50 text-rose-700 border-rose-200",
  Specialist:       "bg-indigo-50 text-indigo-700 border-indigo-200",
};

const priceTier = {
  premium:  { label: "Premium",  chip: "bg-slate-900 text-white" },
  standard: { label: "Standard", chip: "bg-slate-100 text-slate-700 border border-slate-200" },
  low:      { label: "Volume",   chip: "bg-indigo-50 text-indigo-700 border border-indigo-200" },
};

export const PartnerCard = ({ partner }) => {
  const tier = priceTier[partner.priceTier];
  const isWatch = partner.badge === "Watch";

  return (
    <article
      data-testid={`partner-${partner.id}`}
      className={`relative rounded-3xl border bg-white cf-shadow-lg overflow-hidden transition-all duration-200 hover:-translate-y-[2px] hover:shadow-[0_22px_50px_rgba(15,23,42,0.08)] ${
        isWatch ? "border-rose-200/70" : "border-slate-200"
      }`}
    >
      {/* Top strip */}
      <div className="p-5 flex items-start gap-4">
        <div className={`w-14 h-14 rounded-2xl ${logoTone[partner.logoColor]} flex items-center justify-center font-display text-[16px] font-bold tracking-wide`}>
          {partner.logoText}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-[16px] font-bold text-slate-900 leading-tight truncate">{partner.name}</h3>
            {partner.badge && (
              <span className={`text-[10px] font-semibold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${badgeMeta[partner.badge]}`}>{partner.badge}</span>
            )}
          </div>
          <p className="text-[11.5px] text-slate-500 mt-0.5">{partner.tagline}</p>
          <div className="mt-2 flex items-center gap-3 text-[11.5px]">
            <span className="inline-flex items-center gap-1 font-semibold text-slate-900">
              <Star size={11} className="fill-amber-400 text-amber-400" /> {partner.rating}
              <span className="text-slate-400 font-normal">({partner.reviews})</span>
            </span>
            <span className="inline-flex items-center gap-1 text-slate-500">
              <Clock size={11} /> {partner.responseHrs}h response
            </span>
          </div>
        </div>
        <button data-testid={`partner-${partner.id}-fav`} className="text-slate-400 hover:text-rose-500 transition-colors">
          <Heart size={16} />
        </button>
      </div>

      {/* AI flag */}
      <div className={`mx-5 mb-3 px-3 py-2 rounded-lg flex items-start gap-2 ${
        isWatch ? "bg-rose-50/60 border border-rose-200" : "bg-indigo-50/60 border border-indigo-200"
      }`}>
        {isWatch ? (
          <AlertOctagon size={12} className="text-rose-600 mt-0.5 shrink-0" />
        ) : (
          <Sparkles size={12} className="text-indigo-600 mt-0.5 shrink-0" />
        )}
        <p className={`text-[11.5px] font-medium ${isWatch ? "text-rose-700" : "text-indigo-700"}`}>{partner.flag}</p>
      </div>

      {/* Metric grid */}
      <div className="px-5 grid grid-cols-3 gap-2">
        <Metric icon={Clock}      label="Avg days" value={`${partner.avgDays}d`} sub="to exchange/job" />
        <Metric icon={Trophy}     label="Success"  value={`${partner.successPct}%`} sub="completion" />
        <Metric icon={TrendingUp} label="Conversion" value={`${partner.conversionPct}%`} sub="referral → live" />
      </div>

      {/* Specialisms */}
      <div className="px-5 pt-4 flex flex-wrap gap-1.5">
        {partner.specialisms.map((s) => (
          <span key={s} className="text-[10.5px] font-semibold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded">{s}</span>
        ))}
      </div>

      {/* Footer */}
      <div className="mt-4 px-5 py-3 border-t border-slate-100 bg-slate-50/40 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px]">
          <span className={`text-[10px] font-semibold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full ${tier.chip}`}>{tier.label}</span>
          <span className="text-slate-500">{partner.activeTx} active</span>
          <span className="text-slate-400">·</span>
          <span className="text-indigo-700 font-semibold">{partner.revenueShare} rev share</span>
        </div>
        <button data-testid={`partner-${partner.id}-refer`} className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[12px] font-semibold transition-colors">
          <Send size={12} /> Refer
        </button>
      </div>
    </article>
  );
};

const Metric = ({ icon: Icon, label, value, sub }) => (
  <div className="rounded-xl border border-slate-100 bg-white px-3 py-2.5">
    <div className="flex items-center gap-1.5 text-slate-500">
      <Icon size={11} />
      <p className="text-[9.5px] uppercase tracking-[0.14em] font-semibold">{label}</p>
    </div>
    <p className="font-display text-[15px] font-bold text-slate-900 mt-1 leading-none">{value}</p>
    <p className="text-[10px] text-slate-400 mt-1">{sub}</p>
  </div>
);
